/*
 **	OMNI_IQ (c) 2025 Silver Falls Capital. All Rights Reserved
 **
 **	This code is unlicensed and may not be copied, used, modified,
 **	compiled, run, or distributed in any form without the explicit
 **	written permission of Silver Falls Capital.
 **
 **/

/**
 * CalibrationLayout - Minimal application shell for the calibration-only app.
 * Provides a header (logo + signed-in user + sign out) and the content area.
 * Replaces the full OmniIQ console layout (DashboardV2 / AppLayout).
 */
import type React from 'react'
import { LogOut } from 'lucide-react'
import omniiq_logo from '../assets/omniiq_logo.png'
import { theme } from '../theme'
import type { ApplicationContextType } from '../components/common/types'

interface CalibrationLayoutProps {
  context: ApplicationContextType
  onLogout: () => void
  children: React.ReactNode
}

export const PaddedContent = ({ children }: { children: React.ReactNode }) => {
  return (
    <main className="flex-1" style={{ background: 'var(--oca-content-background)' }}>
      <div className="mx-auto px-1 pb-32 sm:px-2 lg:px-4">
        <div
          className="ml-2 mr-2 rounded-sm min-height-content px-2 py-6 sm:px-3"
          style={{ background: 'var(--oca-content-background)' }}
        >
          {children}
        </div>
      </div>
    </main>
  )
}

const CalibrationLayout: React.FC<CalibrationLayoutProps> = ({ context, onLogout, children }) => {
  const userName = context.userProfile?.name || context.userProfile?.email || ''

  const headerStyle: React.CSSProperties = {
    height: '64px',
    borderBottom: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.surface,
    position: 'sticky',
    top: 0,
    zIndex: theme.zIndex.sticky,
  }

  const headerInnerStyle: React.CSSProperties = {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    maxWidth: '1600px',
    margin: '0 auto',
    padding: `0 ${theme.spacing.xl}`,
    boxSizing: 'border-box',
  }

  const contentStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '1600px',
    margin: '0 auto',
    padding: `0 ${theme.spacing.xl}`,
    boxSizing: 'border-box',
  }

  const signOutStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.md,
    background: 'transparent',
    color: theme.colors.text.secondary,
    cursor: 'pointer',
    ...theme.typography.body,
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: theme.colors.background }}>
      <header style={headerStyle}>
        <div style={headerInnerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
            <img src={omniiq_logo} alt="OmniIQ" style={{ height: '32px', width: 'auto' }} />
            <span style={{ ...theme.typography.h2, margin: 0, color: theme.colors.text.primary }}>Calibration</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
            {userName && (
              <span style={{ ...theme.typography.body, color: theme.colors.text.secondary }}>{userName}</span>
            )}
            <button type="button" style={signOutStyle} onClick={onLogout} aria-label="Sign out">
              <LogOut size={16} />
              Sign out
            </button>
          </div>
        </div>
      </header>
      <div style={contentStyle}>{children}</div>
    </div>
  )
}

export default CalibrationLayout
